import { collabKey } from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import { type EditorState } from "prosemirror-state";
import { type Mappable } from "prosemirror-transform";
import { useMemo } from "react";

import type { PresenceIndicator } from "../../internal/prosemirror/presence.types";

export type MappedPresenceIndicator = PresenceIndicator & {
	/** The rebased anchor position in the current document */
	anchor: number;
	/** The rebased head position in the current document */
	head: number;
};

export function useMappedPresenceIndicators(
	editorState: EditorState | null | undefined,
	presenceIndicators: Record<string, PresenceIndicator[]> | undefined,
	stepMaps: { version: number; mappables: Mappable[] }[] | undefined
) {
	return useMemo(() => {
		if (!editorState) {
			return null;
		}

		const collabState = collabKey.getState(editorState);

		if (!collabState) {
			return null;
		}

		return Object.values(presenceIndicators ?? {})
			.map((indicators) => {
				const { unconfirmed, version } = collabState;
				const indicator = indicators.toReversed().find((i) => i.version <= version);

				if (!indicator) {
					return null;
				}

				const confirmedMappables = (stepMaps ?? [])
					.filter(({ version }) => version > indicator.version)
					.flatMap(({ mappables }) => mappables);

				// This indicator may predate when we started tracking stepMaps.
				// If so, we have to ignore it, because we can't map it forward.
				if (confirmedMappables.length < version - indicator.version) {
					return null;
				}

				const mappables = confirmedMappables.concat(
					unconfirmed.map(({ step }) => step.getMap())
				);

				const anchor = mappables.reduce(
					(acc, mappable) => mappable.map(acc),
					indicator.anchor
				);
				const head = mappables.reduce((acc, mappable) => mappable.map(acc), indicator.head);

				return { ...indicator, anchor, head };
			})
			.filter((indicator) => !!indicator);
	}, [editorState, presenceIndicators, stepMaps]);
}
